import type { Rec } from "./records";

/**
 * Who may write a collection's records (docs/views.md#2-data). A collection is `chat` scoped unless its manifest says
 * otherwise, and writable by anyone who may read the record's chat; the author or an admin removes a record. A
 * collection whose rule says `action` takes writes from the view's actions, whose code decides who may do what.
 */
export type Scope = "chat" | "view" | "personal";
export type WriteWord = "member" | "admin" | "author" | "action" | "integration" | "runner";
export interface CollectionRules {
  scope: Scope;
  write: WriteWord[];
  key?: "person";
}
export type Role = "admin" | "member";

/** Where a record lives: a chat's partition, the view's own, or one person's. */
export type Partition = { scope: "chat"; chat: string } | { scope: "view" } | { scope: "personal"; person: string };
export const partitionKey = (partition: Partition) =>
  partition.scope === "chat"
    ? `chat:${partition.chat}`
    : partition.scope === "view"
      ? "view"
      : `personal:${partition.person}`;
export function parsePartition(key: string): Partition | null {
  if (key === "view") return { scope: "view" };
  const match = /^(chat|personal):([A-Za-z0-9_-]{1,64})$/.exec(key);
  if (!match) return null;
  const [, scope, id] = match;
  if (!scope || !id) return null;
  return scope === "chat" ? { scope: "chat", chat: id } : { scope: "personal", person: id };
}

/**
 * Who is writing, as the ticket or token they came with says. A person's agent writes as that person, `via` saying so;
 * an integration or a runner writes only what its collection's rule names. `role` is the person's role for a chat's
 * data, which counts an organization's open chats and its admins (docs/accounts.md#10-organizations); for the view's own partition,
 * `role(null)` says whether they can reach the view at all.
 */
export interface Writer {
  id: string;
  /** `view` is the view's own code, run by a trigger, which writes its chats' records and its own. */
  kind: "person" | "view" | "integration" | "runner";
  /** Who acts for the writer: a person's agent, or for the view an integration that called it. */
  via?: string;
  /** The view's action this write comes from, run as the writer (docs/views.md#14-queries-and-actions). */
  action?: string;
  role(chat: string | null): Role | null;
  /** The view's admins: its organization's, or the person whose view it is. */
  viewAdmin: boolean;
}

export interface Write {
  kind: "insert" | "update" | "remove";
  id: string;
  before: Rec | null;
}

export function allowed(rules: CollectionRules, partition: Partition, write: Write, writer: Writer): boolean {
  if (writer.kind === "view") return partition.scope !== "personal";
  if (writer.kind !== "person") return rules.write.includes(writer.kind);
  if (partition.scope === "personal") return partition.person === writer.id;
  // One record per person, written by that person alone.
  if (rules.key === "person")
    return write.id === writer.id && !!writer.role(partition.scope === "chat" ? partition.chat : null);
  const role = writer.role(partition.scope === "chat" ? partition.chat : null);
  if (!role) return false;
  // Within the writer's reach, an action writes what its collection leaves to actions.
  if (writer.action && rules.write.includes("action")) return true;
  const admin = partition.scope === "chat" ? role === "admin" : writer.viewAdmin;
  const author = write.kind === "insert" || write.before?.createdBy === writer.id;
  if (write.kind === "remove") return admin || write.before?.createdBy === writer.id;
  if (rules.write.includes("member")) return true;
  if (rules.write.includes("admin") && admin) return true;
  if (rules.write.includes("author") && author) return true;
  return false;
}

/**
 * Where a connection's data is (docs/views.md#1-views): the chats it reads, one in a chat and several in the view's home, and
 * who writes, with their role in each chat when the home gives them different ones.
 */
export interface Place {
  chats: string[];
  writer: { id: string; role: Role | null; viewAdmin: boolean; roles?: Record<string, Role> };
}

/** The partitions a collection's records live in for a connection: each of its chats', the view's, or the person's own. */
export function partsFor(rules: Pick<CollectionRules, "scope">, place: Place): string[] {
  if (rules.scope === "chat") return place.chats.map((chat) => partitionKey({ scope: "chat", chat }));
  return [rules.scope === "view" ? "view" : partitionKey({ scope: "personal", person: place.writer.id })];
}

/**
 * The one partition a write goes to: for a chat's collection, the record's own chat or the chat the write names, or the
 * only chat there is; null when the home has several and the write names none, or names one it doesn't read.
 */
export function partFor(rules: Pick<CollectionRules, "scope">, place: Place, chat?: string | null): string | null {
  if (rules.scope !== "chat") return partsFor(rules, place)[0]!;
  const target = chat ?? (place.chats.length === 1 ? place.chats[0]! : null);
  return target && place.chats.includes(target) ? partitionKey({ scope: "chat", chat: target }) : null;
}

/** The writer a connection writes as: its role in each of its chats, and the best of them for the view's own records. */
export function placeWriter(place: Place): Writer {
  const { writer, chats } = place;
  return {
    id: writer.id,
    kind: "person",
    viewAdmin: writer.viewAdmin,
    role: (chat) =>
      chat === null
        ? writer.role
        : writer.roles
          ? (writer.roles[chat] ?? null)
          : chats.includes(chat)
            ? writer.role
            : null,
  };
}
